import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useBeforeUnload,
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';

import { ApiError } from '../api/errors';
import { SeverityBadge } from '../alerts/SeverityBadge';
import { useAppBar } from '../app/AppBar';
import appStyles from '../app/app.module.css';
import { SESSION_QUERY_KEY } from '../auth/SessionProvider';
import { fetchNotes } from '../history/api';
import { NOTE_STATUS_LABELS, type NoteStatus } from '../notes/status';
import { ErrorScreen } from '../shell/ErrorScreen';
import { Icon } from '../ui/Icon';
import { Sheet } from '../ui/Sheet';
import { AnnotationsPanel } from './AnnotationsPanel';
import { discardNote, restoreNote } from '../history/api';
import { fetchNoteDetail, markReady, saveReview } from './api';
import { calculatedLineAmount, compareMoney, sumLineAmounts } from './arithmetic';
import { DeliverySummary } from './DeliverySummary';
import {
  addAnnotation,
  addLine,
  countDirtyFields,
  documentRequestFromDraft,
  draftFromDocument,
  draftValidationErrors,
  isDraftDirty,
  moveLineDown,
  moveLineUp,
  removeAnnotation,
  removeLine,
  updateCustomerReference,
  updateCustomerText,
  updateFolio,
  updateLine,
  updateLineProductReference,
  updateNoteDate,
  updateWrittenTotal,
  type ReferenceResolution,
  type ReviewDraft,
} from './document';
import { EvidenceSheet } from './EvidenceSheet';
import { fieldAnchorId } from './fieldAnchors';
import { alertMessageLabel, confidenceBandLabel, findingLabel } from './findings';
import { totalMismatchPhraseIfDisagreeing } from './mismatchExplain';
import { classifyReadyFailure } from './readyFailure';
import { ResolutionSheet, type ResolutionStep } from './ResolutionSheet';
import { buildResolutionQueue, type ResolvableItem } from './resolutionQueue';
import { classifySaveFailure } from './saveFailure';
import {
  EDITABLE_NOTE_STATUSES,
  type NoteDetail,
  type Prediction,
  type ReferenceIssue,
} from './schema';
import styles from './review.module.css';

/**
 * Where "Descartar nota" is offered. The three missing statuses are the ones where Excel
 * already has the sale or is being handed it, and the backend refuses them too
 * (`app.domain.note_state.DISCARDABLE_STATUSES`) - this set only decides whether to show the
 * button, never whether the action is allowed.
 */
const DISCARDABLE_NOTE_STATUSES: ReadonlySet<NoteStatus> = new Set([
  'submitted',
  'processing',
  'awaiting_review',
  'blocked_reference',
  'failed',
  'ready_for_excel',
]);

/** Note statuses where the owner may still take **Lista para Excel**. */
const READY_ELIGIBLE_STATUSES: ReadonlySet<NoteStatus> = new Set([
  'awaiting_review',
  'blocked_reference',
]);

/** Statuses whose bar pill takes the warm alarm ink and a leading icon. A danger tint would
 * disappear on the bar's dark green, so the tone inverts there (`visual-system.md` §4). */
const BAR_ALARM_STATUSES: ReadonlySet<NoteStatus> = new Set([
  'blocked_reference',
  'delivery_attention',
  'failed',
]);

const ALERT_BANNER_CLASS: Readonly<Record<string, string | undefined>> = {
  info: styles.bannerInfo,
  warning: styles.bannerWarning,
  error: styles.bannerError,
};

interface Session {
  readonly detail: NoteDetail;
  readonly draft: ReviewDraft;
}

interface ConflictState {
  readonly latest: NoteDetail;
}

/**
 * One walk through the "N cosas por resolver" queue. `items` is a snapshot taken when the walk
 * starts, deliberately not the live queue: the live one loses each field the moment it is
 * resolved, which would make the `K de N` header count down as the owner works instead of
 * telling them how far along they are. Nothing here advances on its own - `index` only ever
 * moves because the owner pressed the sheet's continue button.
 */
interface ResolutionRun {
  readonly items: readonly ResolvableItem[];
  readonly index: number;
}

/** The answer the owner just gave for one field, which is what turns the sheet's continue
 * control on. `marked_new` fields keep `official_name === null`, so the draft alone cannot say
 * whether they have been dealt with. */
interface ResolutionOutcome {
  readonly fieldPath: string;
  readonly kind: 'resolved' | 'marked_new';
  readonly officialName: string | null;
}

const READ_ONLY_EXPLANATIONS: Readonly<Partial<Record<NoteStatus, string>>> = {
  submitted: 'Esta nota todavía no terminó de procesarse. Vuelve en unos segundos.',
  processing: 'Esta nota todavía se está reconociendo. Vuelve en unos segundos.',
  ready_for_excel:
    'Esta nota ya fue aprobada y está lista para Excel, así que no se puede editar aquí.',
  delivering: 'Esta nota se está enviando a Excel en este momento.',
  delivery_attention: 'El envío de esta nota a Excel necesita tu atención antes de continuar.',
  failed: 'El reconocimiento de esta nota falló. Reinténtalo desde Notas.',
};

const POST_EXPORT_WARNING = alertMessageLabel('review.post_export_edit');

const NOTE_DATE_FIELD_PATH = '/note_date';

/** `backend/app/validation/findings.py`'s date codes - the ones worth a phrase under the date
 * field, same list `app.validation.dates`'s module doc reasons about (`date_ambiguous`,
 * `date_unparsed`) plus the two plausibility ones (`date_in_future`, `date_too_old`). */
const DATE_FINDING_CODES: ReadonlySet<string> = new Set([
  'date_unparsed',
  'date_ambiguous',
  'date_in_future',
  'date_too_old',
]);

interface LineIssue {
  readonly dotTone: 'warning' | 'danger';
  readonly message: string;
}

/** What the redesign puts under a flagged line: an unresolved concept (blocks Excel delivery),
 * and/or its own quantity x price disagreeing with its written amount *right now* - not a
 * validation flag from the original recognition run, which stays available as evidence
 * (`EvidenceSheet`'s "Incidencias del reconocimiento original") without re-flagging a line the
 * owner has since corrected back into agreement. */
function lineIssues(
  line: ReviewDraft['lines'][number],
  amountDisagrees: boolean,
): readonly LineIssue[] {
  const issues: LineIssue[] = [];
  if (line.product.officialName === null && line.product.recognizedText.trim() !== '') {
    issues.push({ dotTone: 'danger', message: 'El concepto no existe en Excel.' });
  }
  if (amountDisagrees) {
    issues.push({ dotTone: 'warning', message: findingLabel('line_amount_disagrees') });
  }
  return issues;
}

export function ReviewScreen() {
  const params = useParams<{ id: string }>();
  const noteId = params.id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const noteQuery = useQuery({
    queryKey: ['note', noteId],
    queryFn: () => fetchNoteDetail(noteId ?? ''),
    enabled: noteId !== undefined,
  });

  const [session, setSession] = useState<Session | null>(null);
  const [loadedForNoteId, setLoadedForNoteId] = useState<string | undefined>(undefined);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const [savedJustNow, setSavedJustNow] = useState(false);
  const [fullImageOpen, setFullImageOpen] = useState(false);
  const [readyConfirmOpen, setReadyConfirmOpen] = useState(false);
  const [readyErrorMessage, setReadyErrorMessage] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [discardErrorMessage, setDiscardErrorMessage] = useState<string | null>(null);
  const [readyJustNow, setReadyJustNow] = useState(false);
  const [exportedEditUnlocked, setExportedEditUnlocked] = useState(false);
  const [resolutionField, setResolutionField] = useState<ResolvableItem | null>(null);
  const [resolutionRun, setResolutionRun] = useState<ResolutionRun | null>(null);
  const [resolutionOutcome, setResolutionOutcome] = useState<ResolutionOutcome | null>(null);
  const [newlyMarkedMessage, setNewlyMarkedMessage] = useState<string | null>(null);
  const [evidenceLineKey, setEvidenceLineKey] = useState<string | null>(null);
  const readySubmittingRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();

  if (noteId !== loadedForNoteId) {
    setLoadedForNoteId(noteId);
    setSession(null);
    setConflict(null);
    setSaveErrorMessage(null);
    setSavedJustNow(false);
    setReadyConfirmOpen(false);
    setReadyErrorMessage(null);
    setReadyJustNow(false);
    setExportedEditUnlocked(false);
    setResolutionField(null);
    setResolutionRun(null);
    setResolutionOutcome(null);
    setEvidenceLineKey(null);
  } else if (session === null && noteQuery.data !== undefined) {
    setSession({
      detail: noteQuery.data,
      draft: draftFromDocument(noteQuery.data.document, noteQuery.data.predictions),
    });
  }

  const draft = session?.draft ?? null;
  const detail = session?.detail ?? null;

  const dirty =
    session !== null &&
    isDraftDirty(session.draft, session.detail.document, session.detail.predictions);
  const dirtyCount =
    session !== null
      ? countDirtyFields(session.draft, session.detail.document, session.detail.predictions)
      : 0;
  // A discarded note is readable and nothing else: every write route refuses it until it is
  // restored, so the screen must not offer an edit that the server would only turn down.
  const isDiscarded = detail !== null && detail.discarded_at !== null;
  const statusEditable =
    detail !== null &&
    !isDiscarded &&
    (EDITABLE_NOTE_STATUSES.has(detail.status) ||
      (detail.status === 'exported' && exportedEditUnlocked));
  const canOfferReady =
    detail !== null && !isDiscarded && READY_ELIGIBLE_STATUSES.has(detail.status);

  // The design gives this screen a back control, the note's identity, and its status in the bar
  // itself, so status stays visible however far the owner has scrolled (`ui-requirements.md`
  // §4.1). The title stays the customer rather than a fixed "Revisar nota" - it is the one word
  // that tells two notes apart, and the canvas puts it in the bar either way.
  const barTitle =
    draft === null
      ? 'Nota'
      : (draft.customer.officialName ?? (draft.customer.recognizedText || 'Sin cliente'));
  const barSubtitle =
    draft === null
      ? undefined
      : `${draft.noteDate !== '' ? draft.noteDate : 'sin fecha'} · ${String(draft.lines.length)} ${draft.lines.length === 1 ? 'fila' : 'filas'}${detail?.revision != null ? ` · versión ${String(detail.revision.revision_no)}` : ''}`;
  const barStatus = detail?.status ?? null;

  useAppBar(
    () => ({
      title: barTitle,
      subtitle: barSubtitle,
      back: { to: '/notas', label: 'Volver a Notas' },
      trailing:
        barStatus === null ? undefined : (
          <span
            className={[
              appStyles.headerStatusPill,
              BAR_ALARM_STATUSES.has(barStatus) ? appStyles.headerStatusPillAlarm : null,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {BAR_ALARM_STATUSES.has(barStatus) ? (
              <Icon name="alert-triangle" size={13} strokeWidth={2.4} />
            ) : null}
            {NOTE_STATUS_LABELS[barStatus]}
          </span>
        ),
    }),
    [barTitle, barSubtitle, barStatus],
  );

  const validationErrors = useMemo(
    () => (draft !== null ? draftValidationErrors(draft) : []),
    [draft],
  );
  const errorByPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const error of validationErrors) {
      map.set(error.fieldPath, error.message);
    }
    return map;
  }, [validationErrors]);

  const predictionsByPath = useMemo(() => {
    const map = new Map<string, Prediction>();
    if (detail !== null) {
      for (const prediction of detail.predictions) {
        map.set(prediction.field_path, prediction);
      }
    }
    return map;
  }, [detail]);

  const issuesByFieldPath = useMemo(() => {
    const map = new Map<string, ReferenceIssue>();
    if (detail !== null) {
      for (const issue of detail.reference_issues) {
        map.set(issue.field_path, issue);
      }
    }
    return map;
  }, [detail]);

  const resolutionQueue = draft !== null ? buildResolutionQueue(draft) : [];

  // The answer only counts for the field actually on screen: reopening a different field leaves
  // the previous outcome behind until the walk clears it.
  const outcomeForOpenField =
    resolutionField !== null && resolutionOutcome?.fieldPath === resolutionField.fieldPath
      ? resolutionOutcome
      : null;
  const onLastRunItem =
    resolutionRun !== null && resolutionRun.index === resolutionRun.items.length - 1;
  // The walk is finished but still open, waiting for the owner to close it or step to another
  // note - the only moment the cross-note lookup below is worth spending a request on.
  const runAwaitingFinish = onLastRunItem && outcomeForOpenField !== null;

  /** The next note anywhere that still needs resolving, offered as a link when a walk ends.
   * Deliberately never navigated to automatically: this note usually has unsaved resolutions on
   * it at this point, and leaving it is the owner's decision to make. A failed lookup simply
   * offers nothing. */
  const nextBlockedNoteQuery = useQuery({
    queryKey: ['notes', 'next-blocked-reference'],
    queryFn: () => fetchNotes({ status: 'blocked_reference', limit: 5 }),
    enabled: runAwaitingFinish && noteId !== undefined,
  });
  const nextBlockedNoteId =
    nextBlockedNoteQuery.data?.items.find((item) => item.id !== noteId)?.id ?? null;

  // Why the date input may be blank, or why a pre-filled guess (`draftFromDocument`'s
  // `bestDateCandidate`) is worth a second look - `app.validation.dates`'s own codes, carried on
  // the `/note_date` prediction exactly like every other field's validation flags.
  const dateFindings = (predictionsByPath.get(NOTE_DATE_FIELD_PATH)?.validation_flags ?? []).filter(
    (code) => DATE_FINDING_CODES.has(code),
  );

  // Open the resolution flow automatically for `?resolver=1` (from Notas' "Resolver" button or
  // an alert card) once the note has loaded, and jump to a named field with `?issue=`.
  const autoResolverRequested = searchParams.get('resolver') === '1';
  useEffect(() => {
    if (
      autoResolverRequested &&
      draft !== null &&
      resolutionQueue.length > 0 &&
      resolutionField === null
    ) {
      // Deliberately synchronous: this opens the resolution sheet from the `?resolver=1`
      // deep link (Notas' "Resolver" button, an alert card) as soon as the note has loaded -
      // there is no external system to synchronize with here, only a one-time URL-driven
      // initial state the owner can then freely change.
      startResolutionRun(resolutionQueue, 0);
      const next = new URLSearchParams(searchParams);
      next.delete('resolver');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once draft becomes available
  }, [autoResolverRequested, draft !== null]);

  const focusIssueId = searchParams.get('issue');
  useEffect(() => {
    if (focusIssueId === null || detail === null) {
      return;
    }
    const issue = detail.reference_issues.find((candidate) => candidate.id === focusIssueId);
    if (issue === undefined) {
      return;
    }
    const anchorId = fieldAnchorId(issue.field_path);
    if (anchorId === null) {
      return;
    }
    const element = document.getElementById(anchorId);
    if (typeof element?.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' });
    }
  }, [focusIssueId, detail]);

  const saveMutation = useMutation({
    mutationFn: (input: { revisionToken: string; draftToSave: ReviewDraft }) =>
      saveReview(noteId ?? '', {
        based_on_revision: input.revisionToken,
        document: documentRequestFromDraft(input.draftToSave),
      }),
  });

  const readyMutation = useMutation({
    mutationFn: () => markReady(noteId ?? ''),
  });

  const discardMutation = useMutation({
    mutationFn: (reason: string) => discardNote(noteId ?? '', reason),
  });

  const restoreMutation = useMutation({
    mutationFn: () => restoreNote(noteId ?? ''),
  });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useBeforeUnload((event) => {
    if (dirty) {
      event.preventDefault();
    }
  });

  function mutateDraft(update: (current: ReviewDraft) => ReviewDraft): void {
    setSession((previous) =>
      previous === null ? previous : { ...previous, draft: update(previous.draft) },
    );
    setSavedJustNow(false);
    setSaveErrorMessage(null);
  }

  function predictionFor(fieldPath: string): Prediction | undefined {
    return predictionsByPath.get(fieldPath);
  }

  function issueFor(fieldPath: string): ReferenceIssue | undefined {
    return issuesByFieldPath.get(fieldPath);
  }

  function fieldError(fieldPath: string): string | null {
    return errorByPath.get(fieldPath) ?? null;
  }

  async function handleSave(): Promise<void> {
    if (session === null || !dirty || validationErrors.length > 0) {
      return;
    }
    setSaveErrorMessage(null);
    try {
      const result = await saveMutation.mutateAsync({
        revisionToken: session.detail.revision_token,
        draftToSave: session.draft,
      });
      setSession({ detail: result, draft: draftFromDocument(result.document, result.predictions) });
      setSavedJustNow(true);
    } catch (caught) {
      await handleSaveFailure(caught);
    }
  }

  async function handleSaveFailure(caught: unknown): Promise<void> {
    if (!(caught instanceof ApiError)) {
      throw caught;
    }
    const kind = classifySaveFailure(caught);
    if (kind === 'session_expired') {
      await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
      return;
    }
    if (kind === 'revision_conflict') {
      try {
        const latest = await fetchNoteDetail(noteId ?? '');
        setConflict({ latest });
      } catch {
        setSaveErrorMessage(caught.message);
      }
      return;
    }
    if (kind === 'not_reviewable') {
      try {
        const latest = await fetchNoteDetail(noteId ?? '');
        setSession((previous) => (previous === null ? previous : { ...previous, detail: latest }));
      } catch {
        // handled below
      }
      setSaveErrorMessage(caught.message);
      return;
    }
    setSaveErrorMessage(caught.message);
  }

  function handleReadyClick(): void {
    if (!canOfferReady || dirty || detail.open_reference_issues > 0) {
      return;
    }
    setReadyErrorMessage(null);
    setReadyConfirmOpen(true);
  }

  function handleCancelReady(): void {
    setReadyConfirmOpen(false);
  }

  async function handleConfirmReady(): Promise<void> {
    if (readySubmittingRef.current) {
      return;
    }
    readySubmittingRef.current = true;
    setReadyErrorMessage(null);
    try {
      await readyMutation.mutateAsync();
      readySubmittingRef.current = false;
      setReadyConfirmOpen(false);
      setReadyJustNow(true);
      const latest = await fetchNoteDetail(noteId ?? '');
      setSession({ detail: latest, draft: draftFromDocument(latest.document, latest.predictions) });
    } catch (caught) {
      readySubmittingRef.current = false;
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      const kind = classifyReadyFailure(caught);
      if (kind === 'session_expired') {
        setReadyConfirmOpen(false);
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
        return;
      }
      setReadyErrorMessage(caught.message);
    }
  }

  /** Re-read the note after a discard or a restore, so the screen shows what actually
   * happened rather than what was asked for. Both change more than one field of the detail -
   * the marker, the delivery, the queued work - and none of it is derivable here. */
  async function reloadDetail(): Promise<void> {
    const latest = await fetchNoteDetail(noteId ?? '');
    setSession({ detail: latest, draft: draftFromDocument(latest.document, latest.predictions) });
  }

  async function handleConfirmDiscard(): Promise<void> {
    setDiscardErrorMessage(null);
    try {
      await discardMutation.mutateAsync(discardReason);
      setDiscardConfirmOpen(false);
      setDiscardReason('');
      // Every list the note was in is now wrong, including the discarded one it just joined.
      await queryClient.invalidateQueries({ queryKey: ['notes'] });
      await reloadDetail();
    } catch (caught) {
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      setDiscardErrorMessage(caught.message);
    }
  }

  async function handleRestore(): Promise<void> {
    setDiscardErrorMessage(null);
    try {
      await restoreMutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: ['notes'] });
      await reloadDetail();
    } catch (caught) {
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      setDiscardErrorMessage(caught.message);
    }
  }

  async function handleKeepMineAndRetry(): Promise<void> {
    if (conflict === null || session === null) {
      return;
    }
    const latestRevisionToken = conflict.latest.revision_token;
    setConflict(null);
    try {
      const result = await saveMutation.mutateAsync({
        revisionToken: latestRevisionToken,
        draftToSave: session.draft,
      });
      setSession({ detail: result, draft: draftFromDocument(result.document, result.predictions) });
      setSavedJustNow(true);
    } catch (caught) {
      await handleSaveFailure(caught);
    }
  }

  function handleDiscardMineAndReload(): void {
    if (conflict === null) {
      return;
    }
    setSession({
      detail: conflict.latest,
      draft: draftFromDocument(conflict.latest.document, conflict.latest.predictions),
    });
    setConflict(null);
    setSaveErrorMessage(null);
  }

  function handleDiscardChanges(): void {
    if (session === null) {
      return;
    }
    setSession({
      detail: session.detail,
      draft: draftFromDocument(session.detail.document, session.detail.predictions),
    });
    setSaveErrorMessage(null);
  }

  /** Opens the resolution sheet on `items[index]` and starts a walk there, so the sheet shows
   * `K de N` and offers a continue control after each answer. `items` is snapshotted here (see
   * `ResolutionRun`). */
  function startResolutionRun(items: readonly ResolvableItem[], index: number): void {
    const first = items[index];
    if (first === undefined) {
      return;
    }
    setResolutionRun({ items, index });
    setResolutionOutcome(null);
    setResolutionField(first);
  }

  /** Opens one field on its own - a resolved field's chevron - with no walk around it. */
  function openResolutionField(item: ResolvableItem): void {
    setResolutionRun(null);
    setResolutionOutcome(null);
    setResolutionField(item);
  }

  /** The inline "Resolver" beside a still-unresolved field. Starts the walk at that field rather
   * than at the top of the queue, so the owner can carry on through whatever else this note
   * needs without going back to the banner. */
  function startResolutionRunAtField(item: ResolvableItem): void {
    const index = resolutionQueue.findIndex((entry) => entry.fieldPath === item.fieldPath);
    if (index === -1) {
      openResolutionField(item);
      return;
    }
    startResolutionRun(resolutionQueue, index);
  }

  /** The sheet's "Siguiente" control. Only ever called from the owner's own tap: an answered
   * field stays on screen until then, so a mis-tap can still be undone and an alias confirmed
   * against the evidence that is still in view. */
  function goToNextResolutionItem(): void {
    if (resolutionRun === null) {
      return;
    }
    const nextIndex = resolutionRun.index + 1;
    const next = resolutionRun.items[nextIndex];
    if (next === undefined) {
      closeResolutionSheet();
      return;
    }
    setResolutionOutcome(null);
    setResolutionRun({ items: resolutionRun.items, index: nextIndex });
    setResolutionField(next);
  }

  function closeResolutionSheet(): void {
    setResolutionRun(null);
    setResolutionOutcome(null);
    setResolutionField(null);
  }

  function handleResolutionChange(
    item: ResolvableItem,
    resolution: ReferenceResolution | null,
  ): void {
    if (item.kind === 'customer') {
      mutateDraft((current) => updateCustomerReference(current, resolution));
    } else if (item.lineKey !== null) {
      mutateDraft((current) => updateLineProductReference(current, item.lineKey ?? '', resolution));
    }
    if (resolution === null) {
      // "Quitar resolución" takes the answer back, so the walk waits on this field again.
      setResolutionOutcome((current) => (current?.fieldPath === item.fieldPath ? null : current));
      return;
    }
    setResolutionOutcome({
      fieldPath: item.fieldPath,
      kind: 'resolved',
      officialName: resolution.officialName,
    });
  }

  function handleMarkNew(item: ResolvableItem): void {
    setNewlyMarkedMessage(
      `${item.label}: marcado como nuevo. Se te avisará para confirmarlo cuando la lista de Excel se sincronice.`,
    );
    setResolutionOutcome({ fieldPath: item.fieldPath, kind: 'marked_new', officialName: null });
  }

  if (noteId === undefined) {
    return <ErrorScreen title="Nota no encontrada" message="No se especificó qué nota abrir." />;
  }

  if (noteQuery.isPending || (noteQuery.isSuccess && session === null)) {
    return (
      <div className={styles.screen} role="status">
        Cargando nota…
      </div>
    );
  }

  if (noteQuery.isError) {
    if (noteQuery.error instanceof ApiError && noteQuery.error.status === 404) {
      return (
        <div className={styles.screen}>
          <Link className={styles.backLink} to="/notas">
            ← Volver a Notas
          </Link>
          <h1 className={styles.title}>Nota no encontrada</h1>
          <p>Esta nota no existe o no te pertenece.</p>
        </div>
      );
    }
    return (
      <ErrorScreen
        message="No se pudo cargar esta nota. Revisa tu conexión e intenta de nuevo."
        detail={noteQuery.error instanceof Error ? noteQuery.error.message : undefined}
        onRetry={() => {
          void noteQuery.refetch();
        }}
      />
    );
  }

  if (detail === null || draft === null) {
    return (
      <div className={styles.screen} role="status">
        Cargando nota…
      </div>
    );
  }

  const noteAnnotations = draft.annotations.filter((annotation) => annotation.lineKey === null);
  const lineAmounts = draft.lines.map((line) => (line.lineAmount === '' ? null : line.lineAmount));
  const liveCalculatedTotal = sumLineAmounts(lineAmounts);
  const totalAgreement = compareMoney(
    draft.writtenTotal === '' ? null : draft.writtenTotal,
    liveCalculatedTotal,
  );
  const totalMismatchMessage =
    draft.writtenTotal !== ''
      ? totalMismatchPhraseIfDisagreeing(
          draft.writtenTotal,
          draft.lines.map((line) => ({
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            lineAmount: line.lineAmount,
          })),
        )
      : null;

  const readOnlyExplanation = statusEditable
    ? null
    : (READ_ONLY_EXPLANATIONS[detail.status] ?? null);
  const canSave =
    statusEditable &&
    dirty &&
    validationErrors.length === 0 &&
    conflict === null &&
    !saveMutation.isPending;
  const canMarkReady =
    canOfferReady && !dirty && detail.open_reference_issues === 0 && !readyMutation.isPending;
  const nothingLeftToResolve = detail.open_reference_issues === 0 && resolutionQueue.length === 0;

  const resolutionQueuePosition =
    resolutionField !== null
      ? resolutionQueue.findIndex((item) => item.fieldPath === resolutionField.fieldPath)
      : -1;

  /* The walk's continue control, present only once the field on screen has an answer. The last
   * step ends the walk rather than leaving the note: resolving here only edits the draft, so the
   * owner still has a "Guardar" to press, and the next note is a link they take when ready. */
  const resolutionStep: ResolutionStep | null =
    resolutionRun !== null && outcomeForOpenField !== null
      ? {
          summary:
            outcomeForOpenField.kind === 'marked_new'
              ? 'Marcado como nuevo. Sigue bloqueada hasta que exista en Excel.'
              : `Resuelto: ${outcomeForOpenField.officialName ?? ''}`,
          nextLabel: onLastRunItem
            ? 'Terminar'
            : `Siguiente (${String(resolutionRun.index + 2)} de ${String(resolutionRun.items.length)}) →`,
          onNext: onLastRunItem ? closeResolutionSheet : goToNextResolutionItem,
          secondary:
            onLastRunItem && nextBlockedNoteId !== null
              ? {
                  label: 'Ir a la siguiente nota por resolver →',
                  onClick: () => {
                    closeResolutionSheet();
                    void navigate(`/notas/${nextBlockedNoteId}?resolver=1`);
                  },
                }
              : undefined,
        }
      : null;

  const evidenceLine =
    evidenceLineKey !== null
      ? draft.lines.find((line) => line.lineKey === evidenceLineKey)
      : undefined;
  const evidenceLinePosition =
    evidenceLineKey !== null
      ? draft.lines.findIndex((line) => line.lineKey === evidenceLineKey) + 1
      : 0;

  return (
    <div className={styles.screen}>
      {/* The note's photo, as the canvas draws it: a thumbnail beside its label and the one
          explicit action that opens the full image (`ui-requirements.md` §4.5, §10.3). */}
      {detail.image !== null ? (
        <div className={styles.photoRow}>
          <button
            type="button"
            className={styles.headerThumbButton}
            onClick={() => {
              setFullImageOpen(true);
            }}
          >
            <img
              className={styles.headerThumb}
              src={`/notes/${detail.id}/image`}
              alt="Foto de la nota"
            />
          </button>
          <div className={styles.photoRowBody}>
            <p className={styles.photoRowTitle}>Foto de la nota</p>
            <p className={styles.photoRowMeta}>Original sin recortar</p>
          </div>
          <button
            type="button"
            className={styles.photoRowAction}
            onClick={() => {
              setFullImageOpen(true);
            }}
          >
            Ver completa
          </button>
        </div>
      ) : null}

      {isDiscarded ? (
        <div className={styles.bannerWarning} role="status">
          <p>
            Descartaste esta nota
            {detail.discarded_reason !== null ? `: "${detail.discarded_reason}"` : '.'} No se borró
            nada: la foto y todo lo que se leyó siguen guardados.
          </p>
          {discardErrorMessage !== null ? (
            <p className={styles.bannerError} role="alert">
              {discardErrorMessage}
            </p>
          ) : null}
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={restoreMutation.isPending}
              onClick={() => void handleRestore()}
            >
              {restoreMutation.isPending ? 'Recuperando…' : 'Recuperar nota'}
            </button>
          </div>
        </div>
      ) : null}

      {readOnlyExplanation !== null && !isDiscarded ? (
        <p className={styles.readOnlyNotice} role="status">
          {readOnlyExplanation}
        </p>
      ) : null}

      {detail.status === 'exported' ? (
        <div className={styles.bannerWarning} role="status">
          <p>{POST_EXPORT_WARNING}</p>
          {!exportedEditUnlocked ? (
            <div className={styles.exportedCorrectionBar}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => {
                  setExportedEditUnlocked(true);
                }}
              >
                Corregir de todas formas
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {detail.alerts.length > 0 ? (
        <ul className={styles.alertsList}>
          {detail.alerts.map((alert) => (
            <li
              key={alert.id}
              className={`${ALERT_BANNER_CLASS[alert.severity] ?? ''} ${styles.alertCard ?? ''}`}
            >
              <div role={alert.severity === 'error' ? 'alert' : 'status'}>
                <div className={styles.alertCardHeader}>
                  <SeverityBadge severity={alert.severity} />
                </div>
                <p>
                  {alertMessageLabel(alert.message_key, {
                    kind: alert.reference_kind,
                    observedText: alert.reference_observed_text,
                  })}
                </p>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {saveErrorMessage !== null ? (
        <p className={styles.bannerError} role="alert">
          {saveErrorMessage}
        </p>
      ) : null}
      {savedJustNow ? (
        <p className={styles.readOnlyNotice} role="status">
          Cambios guardados.
        </p>
      ) : null}
      {readyJustNow ? (
        <p className={styles.readOnlyNotice} role="status">
          La nota quedó lista para Excel.
        </p>
      ) : null}
      {newlyMarkedMessage !== null ? (
        <p className={styles.readOnlyNotice} role="status">
          {newlyMarkedMessage}
        </p>
      ) : null}

      {conflict !== null ? (
        <div className={styles.bannerWarning} role="alert">
          <p>
            Alguien guardó otra versión de esta nota mientras la editabas. Tus cambios no se han
            perdido.
          </p>
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => void handleKeepMineAndRetry()}
            >
              Actualizar y guardar mis cambios
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleDiscardMineAndReload}
            >
              Descartar mis cambios y ver la versión más reciente
            </button>
          </div>
        </div>
      ) : null}

      {blocker.state === 'blocked' ? (
        <div className={styles.bannerWarning} role="alertdialog" aria-label="Cambios sin guardar">
          <p>Tienes cambios sin guardar en esta nota.</p>
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                blocker.reset();
              }}
            >
              Seguir editando
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => {
                blocker.proceed();
              }}
            >
              Salir sin guardar
            </button>
          </div>
        </div>
      ) : null}

      {detail.open_reference_issues > 0 || resolutionQueue.length > 0 ? (
        <div className={styles.resolveBanner} role="status">
          <p className={styles.resolveBannerTitle}>
            {resolutionQueue.length === 1
              ? '1 cosa por resolver'
              : `${String(resolutionQueue.length)} cosas por resolver`}
          </p>
          <p className={styles.resolveBannerText}>Hasta entonces no se puede enviar a Excel.</p>
          {statusEditable ? (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => {
                startResolutionRun(resolutionQueue, 0);
              }}
            >
              Resolver →
            </button>
          ) : null}
        </div>
      ) : null}

      <section className={styles.field} id="field-customer">
        <span className={styles.rowLabel}>Cliente</span>
        <div className={styles.rowValueLine}>
          <span className={styles.rowValueBold}>
            {draft.customer.officialName ?? (draft.customer.recognizedText || 'Sin cliente')}
          </span>
          {draft.customer.officialName === null ? (
            statusEditable ? (
              <button
                type="button"
                className={styles.resolveInlineButton}
                onClick={() => {
                  startResolutionRunAtField({
                    kind: 'customer',
                    fieldPath: '/customer',
                    lineKey: null,
                    label: 'Cliente',
                  });
                }}
              >
                Resolver
              </button>
            ) : null
          ) : (
            <button
              type="button"
              className={styles.chevronButton}
              aria-label="Ver o cambiar cliente"
              onClick={() => {
                openResolutionField({
                  kind: 'customer',
                  fieldPath: '/customer',
                  lineKey: null,
                  label: 'Cliente',
                });
              }}
            >
              ›
            </button>
          )}
        </div>
        {draft.customer.officialName === null && draft.customer.recognizedText.trim() !== '' ? (
          <p className={`${styles.rowIssuePhrase ?? ''} ${styles.rowIssueDanger ?? ''}`}>
            <Icon name="x-circle" size={14} strokeWidth={2.2} className={styles.rowIssueIcon} />
            No existe en Excel todavía
          </p>
        ) : null}
        <input
          type="text"
          className={styles.textCorrectionInput}
          aria-label="Cliente (según la nota)"
          value={draft.customer.recognizedText}
          maxLength={200}
          disabled={!statusEditable}
          placeholder="Texto tal como aparece en la nota"
          onChange={(event) => {
            mutateDraft((current) => updateCustomerText(current, event.target.value));
          }}
        />
      </section>

      <section className={styles.field}>
        <span className={styles.rowLabel}>Fecha</span>
        <input
          type="date"
          className={styles.monoInput}
          aria-label="Fecha de la nota"
          value={draft.noteDate}
          disabled={!statusEditable}
          onChange={(event) => {
            mutateDraft((current) => updateNoteDate(current, event.target.value));
          }}
        />
        {dateFindings.map((code) => (
          <p
            key={code}
            className={`${styles.rowIssuePhrase ?? ''} ${styles.rowIssueWarning ?? ''}`}
          >
            <Icon
              name="alert-triangle"
              size={14}
              strokeWidth={2.2}
              className={styles.rowIssueIcon}
            />
            {findingLabel(code)}
          </p>
        ))}
      </section>

      <section className={styles.field} id="field-folio">
        <span className={styles.rowLabel}>Folio</span>
        <input
          type="text"
          className={styles.monoInput}
          aria-label="Folio de la nota"
          inputMode="numeric"
          maxLength={200}
          value={draft.folio}
          disabled={!statusEditable}
          placeholder="Sin folio"
          onChange={(event) => {
            mutateDraft((current) => updateFolio(current, event.target.value));
          }}
        />
        <p className={styles.folioHint}>Solo para referencia. No se envía a Excel.</p>
      </section>

      <section className={styles.linesSection}>
        <div className={styles.linesSectionHeader}>
          <h2 className={styles.sectionTitle}>Filas</h2>
          {statusEditable ? (
            <button
              type="button"
              className={styles.addLineButton}
              onClick={() => {
                mutateDraft((current) => addLine(current));
              }}
            >
              + Agregar
            </button>
          ) : null}
        </div>

        <ul className={styles.compactLinesList}>
          {draft.lines.map((line, index) => {
            const position = index + 1;
            const productPath = `/lines/${line.lineKey}/product`;
            const quantityPath = `/lines/${line.lineKey}/quantity`;
            const unitPricePath = `/lines/${line.lineKey}/unit_price`;
            const lineAmountPath = `/lines/${line.lineKey}/line_amount`;

            const calculatedAmount = calculatedLineAmount(
              line.quantity === '' ? null : line.quantity,
              line.unitPrice === '' ? null : line.unitPrice,
            );
            const lineAgreement = compareMoney(
              line.lineAmount === '' ? null : line.lineAmount,
              calculatedAmount,
            );
            const issues = lineIssues(line, lineAgreement === 'disagrees');
            const lineAnnotations = draft.annotations.filter(
              (annotation) => annotation.lineKey === line.lineKey,
            );
            const bestConfidence = predictionFor(productPath)?.confidence_band ?? null;
            const clean = issues.length === 0;

            return (
              <li
                key={line.lineKey}
                className={[styles.compactLine, clean ? null : styles.compactLineProblem]
                  .filter(Boolean)
                  .join(' ')}
                id={`field-product-${line.lineKey}`}
              >
                <div className={styles.compactLineHeader}>
                  <span className={styles.linePosition}>{position}</span>
                  <input
                    type="text"
                    className={[
                      styles.productInput,
                      line.product.officialName === null ? styles.productInputUnresolved : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={`Concepto de la fila ${String(position)}`}
                    value={line.product.recognizedText}
                    placeholder="Concepto"
                    disabled={!statusEditable}
                    onChange={(event) => {
                      mutateDraft((current) =>
                        updateLine(current, line.lineKey, { productText: event.target.value }),
                      );
                    }}
                  />
                  {statusEditable ? (
                    <div className={styles.lineControls}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Subir fila ${String(position)}`}
                        disabled={index === 0}
                        onClick={() => {
                          mutateDraft((current) => moveLineUp(current, line.lineKey));
                        }}
                      >
                        <Icon name="arrow-up" size={14} />
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Bajar fila ${String(position)}`}
                        disabled={index === draft.lines.length - 1}
                        onClick={() => {
                          mutateDraft((current) => moveLineDown(current, line.lineKey));
                        }}
                      >
                        <Icon name="arrow-down" size={14} />
                      </button>
                      <button
                        type="button"
                        className={styles.iconButtonDanger}
                        aria-label={`Eliminar fila ${String(position)}`}
                        onClick={() => {
                          mutateDraft((current) => removeLine(current, line.lineKey));
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                  ) : null}
                </div>
                {line.product.officialName !== null ? (
                  <span className={styles.resolvedPill}>Resuelto: {line.product.officialName}</span>
                ) : null}

                <div className={styles.compactLineMath}>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.numberInput}
                    aria-label={`Cantidad de la fila ${String(position)}`}
                    value={line.quantity}
                    disabled={!statusEditable}
                    onChange={(event) => {
                      mutateDraft((current) =>
                        updateLine(current, line.lineKey, { quantity: event.target.value }),
                      );
                    }}
                  />
                  <span aria-hidden="true">×</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.numberInput}
                    aria-label={`Precio unitario de la fila ${String(position)}`}
                    value={line.unitPrice}
                    disabled={!statusEditable}
                    onChange={(event) => {
                      mutateDraft((current) =>
                        updateLine(current, line.lineKey, { unitPrice: event.target.value }),
                      );
                    }}
                  />
                  <span aria-hidden="true">=</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={[
                      styles.numberInput,
                      lineAgreement === 'disagrees' ? styles.numberInputDanger : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={`Importe de la fila ${String(position)}`}
                    value={line.lineAmount}
                    disabled={!statusEditable}
                    onChange={(event) => {
                      mutateDraft((current) =>
                        updateLine(current, line.lineKey, { lineAmount: event.target.value }),
                      );
                    }}
                  />
                </div>
                {fieldError(quantityPath) !== null ? (
                  <span className={styles.fieldError}>{fieldError(quantityPath)}</span>
                ) : null}
                {fieldError(unitPricePath) !== null ? (
                  <span className={styles.fieldError}>{fieldError(unitPricePath)}</span>
                ) : null}
                {fieldError(lineAmountPath) !== null ? (
                  <span className={styles.fieldError}>{fieldError(lineAmountPath)}</span>
                ) : null}

                {clean ? (
                  <button
                    type="button"
                    className={styles.lineCleanNote}
                    onClick={() => {
                      setEvidenceLineKey(line.lineKey);
                    }}
                  >
                    <span>
                      {line.quantity} × {line.unitPrice} = {line.lineAmount}
                      {bestConfidence !== null
                        ? ` · leído con confianza ${confidenceBandLabel(bestConfidence).toLowerCase()}`
                        : ''}
                    </span>
                    <span className={styles.lineCleanVerdict}>
                      <Icon name="check-circle" size={14} strokeWidth={2.4} />
                      Cuadra
                    </span>
                  </button>
                ) : (
                  <div className={styles.lineIssueBlock}>
                    {issues.map((issue, issueIndex) => (
                      <p
                        key={issueIndex}
                        className={[
                          styles.rowIssuePhrase,
                          issue.dotTone === 'danger'
                            ? styles.rowIssueDanger
                            : styles.rowIssueWarning,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <Icon
                          name={issue.dotTone === 'danger' ? 'x-circle' : 'alert-triangle'}
                          size={14}
                          strokeWidth={2.2}
                          className={styles.rowIssueIcon}
                        />
                        {issue.message}
                      </p>
                    ))}
                    {lineAnnotations.length > 0 ? (
                      <p className={styles.annotationQuote}>
                        &quot;{lineAnnotations[0]?.text}&quot;
                      </p>
                    ) : null}
                    <div className={styles.lineIssueActions}>
                      {line.product.officialName === null && statusEditable ? (
                        <button
                          type="button"
                          className={styles.resolveInlineButton}
                          onClick={() => {
                            startResolutionRunAtField({
                              kind: 'product',
                              fieldPath: productPath,
                              lineKey: line.lineKey,
                              label: `Concepto (fila ${String(position)})`,
                            });
                          }}
                        >
                          Resolver
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={styles.secondaryButtonSmall}
                        onClick={() => {
                          setEvidenceLineKey(line.lineKey);
                        }}
                      >
                        Ver evidencia
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className={styles.totalsBlock}>
        <div className={styles.totalsRowMain}>
          <div>
            <span className={styles.rowLabel}>Total escrito</span>
            <input
              type="text"
              inputMode="decimal"
              className={`${styles.totalInput ?? ''} ${fieldError('/written_total') !== null ? (styles.inputInvalid ?? '') : ''}`}
              value={draft.writtenTotal}
              disabled={!statusEditable}
              aria-label="Total escrito en la nota"
              aria-invalid={fieldError('/written_total') !== null}
              onChange={(event) => {
                mutateDraft((current) => updateWrittenTotal(current, event.target.value));
              }}
            />
          </div>
        </div>
        {/* Agreement strip: the comparison on the left, the verdict and its icon on the right,
            both in the tone's ink (`docs/design/visual-system.md` §5). */}
        <div
          className={[
            styles.totalsStrip,
            totalAgreement === 'disagrees' ? styles.totalsStripDanger : styles.totalsStripOk,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span>
            Suma de las filas:{' '}
            {liveCalculatedTotal !== null ? liveCalculatedTotal.toString() : 'sin datos'}
          </span>
          {totalAgreement === 'uncheckable' ? null : (
            <span className={styles.totalsVerdict}>
              <Icon
                name={totalAgreement === 'disagrees' ? 'x-circle' : 'check-circle'}
                size={14}
                strokeWidth={2.4}
              />
              {totalAgreement === 'disagrees' ? 'No cuadra' : 'Cuadra'}
            </span>
          )}
        </div>
        {fieldError('/written_total') !== null ? (
          <span className={styles.fieldError}>{fieldError('/written_total')}</span>
        ) : null}
        {totalMismatchMessage !== null ? (
          <p className={`${styles.rowIssuePhrase ?? ''} ${styles.rowIssueWarning ?? ''}`}>
            <Icon
              name="alert-triangle"
              size={14}
              strokeWidth={2.2}
              className={styles.rowIssueIcon}
            />
            {totalMismatchMessage}
          </p>
        ) : totalAgreement === 'disagrees' ? (
          <p className={`${styles.rowIssuePhrase ?? ''} ${styles.rowIssueWarning ?? ''}`}>
            <Icon
              name="alert-triangle"
              size={14}
              strokeWidth={2.2}
              className={styles.rowIssueIcon}
            />
            El total no cuadra.
          </p>
        ) : null}

        <AnnotationsPanel
          title="Notas generales de la nota"
          annotations={noteAnnotations}
          fieldChoices={[]}
          editable={statusEditable}
          onAdd={(text, fieldName) => {
            mutateDraft((current) => addAnnotation(current, { text, lineKey: null, fieldName }));
          }}
          onRemove={(clientId) => {
            mutateDraft((current) => removeAnnotation(current, clientId));
          }}
        />
      </section>

      {nothingLeftToResolve && canOfferReady ? (
        <div className={styles.readySection}>
          <h2 className={styles.sectionTitle}>Enviar a Excel</h2>
          <p className={styles.readyHint}>
            {dirty
              ? 'Guarda tus cambios antes de continuar.'
              : `Se enviará la versión ${detail.revision !== null ? `#${String(detail.revision.revision_no)}` : 'actual'} tal como está.`}
          </p>
          {readyErrorMessage !== null ? (
            <p className={styles.bannerError} role="alert">
              {readyErrorMessage}
            </p>
          ) : null}
          <button
            type="button"
            className={styles.readyButton}
            disabled={!canMarkReady}
            onClick={handleReadyClick}
          >
            Enviar a Excel
          </button>
          {readyConfirmOpen ? (
            <div
              className={styles.bannerWarning}
              role="alertdialog"
              aria-label="Confirmar envío a Excel"
            >
              <p>
                Vas a aprobar esta nota para Excel. El agente tomará la versión guardada tal como
                está ahora mismo.
              </p>
              <div className={styles.bannerActions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={readyMutation.isPending}
                  onClick={() => void handleConfirmReady()}
                >
                  {readyMutation.isPending ? 'Enviando…' : 'Sí, marcar lista'}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  disabled={readyMutation.isPending}
                  onClick={handleCancelReady}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {detail.ready_revision !== null && detail.ready_document !== null ? (
        <DeliverySummary
          readyRevision={detail.ready_revision}
          readyDocument={detail.ready_document}
          delivery={detail.delivery}
          receipt={detail.receipt}
        />
      ) : null}

      {!isDiscarded && DISCARDABLE_NOTE_STATUSES.has(detail.status) ? (
        <section className={styles.readySection}>
          <h2 className={styles.sectionTitle}>Descartar</h2>
          <p className={styles.readyHint}>
            Quita esta nota de tu lista sin borrar nada. Puedes recuperarla después desde Notas →
            Filtrar → Descartadas.
          </p>
          {discardErrorMessage !== null ? (
            <p className={styles.bannerError} role="alert">
              {discardErrorMessage}
            </p>
          ) : null}
          {discardConfirmOpen ? (
            <div
              className={styles.bannerWarning}
              role="alertdialog"
              aria-label="Confirmar descarte de la nota"
            >
              <p>
                {detail.status === 'ready_for_excel'
                  ? 'Esta nota ya estaba aprobada. Al descartarla se cancela su envío a Excel, que todavía no ocurrió.'
                  : 'Esta nota saldrá de tu lista. Nada se borra y puedes recuperarla.'}
              </p>
              <label className={styles.discardReasonLabel} htmlFor="discard-reason">
                Motivo (opcional)
              </label>
              <input
                id="discard-reason"
                className={styles.discardReasonInput}
                type="text"
                maxLength={500}
                placeholder="foto repetida"
                value={discardReason}
                onChange={(event) => {
                  setDiscardReason(event.target.value);
                }}
              />
              <div className={styles.bannerActions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={discardMutation.isPending}
                  onClick={() => void handleConfirmDiscard()}
                >
                  {discardMutation.isPending ? 'Descartando…' : 'Sí, descartar'}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  disabled={discardMutation.isPending}
                  onClick={() => {
                    setDiscardConfirmOpen(false);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                setDiscardErrorMessage(null);
                setDiscardConfirmOpen(true);
              }}
            >
              Descartar nota
            </button>
          )}
        </section>
      ) : null}

      {statusEditable && dirty ? (
        <div className={styles.actionBar}>
          <span className={styles.dirtyCount}>
            {dirtyCount === 1
              ? '1 cambio sin guardar'
              : `${String(dirtyCount)} cambios sin guardar`}
          </span>
          <button type="button" className={styles.discardLink} onClick={handleDiscardChanges}>
            Descartar
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={!canSave}
            onClick={() => void handleSave()}
          >
            {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      ) : null}

      {fullImageOpen ? (
        <Sheet
          title="Foto completa de la nota"
          layer="foreground"
          onClose={() => {
            setFullImageOpen(false);
          }}
        >
          <img
            className={styles.fullPhoto}
            src={`/notes/${detail.id}/image`}
            alt="Foto completa de la nota"
          />
        </Sheet>
      ) : null}

      {resolutionField !== null ? (
        <ResolutionSheet
          item={resolutionField}
          position={
            resolutionRun === null
              ? null
              : { index: resolutionRun.index, total: resolutionRun.items.length }
          }
          step={resolutionStep}
          noteId={detail.id}
          onClose={closeResolutionSheet}
          onShowFullImage={() => {
            setFullImageOpen(true);
          }}
          footer={
            resolutionQueuePosition !== -1 && statusEditable && outcomeForOpenField === null ? (
              <div className={styles.markNewFooter}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    handleMarkNew(resolutionField);
                  }}
                >
                  Ninguno: es un {resolutionField.kind === 'customer' ? 'cliente' : 'concepto'}{' '}
                  nuevo
                </button>
              </div>
            ) : undefined
          }
          pickerProps={{
            kind: resolutionField.kind,
            value:
              resolutionField.kind === 'customer'
                ? draft.customer
                : (draft.lines.find((line) => line.lineKey === resolutionField.lineKey)?.product ??
                  draft.customer),
            prediction: predictionFor(resolutionField.fieldPath),
            openIssue: issueFor(resolutionField.fieldPath),
            editable: statusEditable,
            onChange: (resolution) => {
              handleResolutionChange(resolutionField, resolution);
            },
          }}
        />
      ) : null}

      {evidenceLineKey !== null && evidenceLine !== undefined ? (
        <EvidenceSheet
          position={evidenceLinePosition}
          noteId={detail.id}
          prediction={
            predictionFor(`/lines/${evidenceLineKey}/line_amount`) ??
            predictionFor(`/lines/${evidenceLineKey}/product`)
          }
          fieldLabel="Fila"
          whyFlagged="Esta fila se marcó porque un dato no coincide con lo esperado."
          onClose={() => {
            setEvidenceLineKey(null);
          }}
          onShowFullImage={() => {
            setFullImageOpen(true);
          }}
          annotations={draft.annotations.filter(
            (annotation) => annotation.lineKey === evidenceLineKey,
          )}
          editable={statusEditable}
          onAddAnnotation={(text) => {
            mutateDraft((current) =>
              addAnnotation(current, { text, lineKey: evidenceLineKey, fieldName: null }),
            );
          }}
          onRemoveAnnotation={(clientId) => {
            mutateDraft((current) => removeAnnotation(current, clientId));
          }}
        />
      ) : null}
    </div>
  );
}
